// ============================================
// Chữ nghĩa + nhãn enum cho màn "Góp ý của khách" (bug #208)
// ============================================
//
// Mọi chuỗi MỚI của màn này đi qua `translateOrFallback` (utils/apiError.ts):
// `useI18n().t` trả về CHÍNH KEY khi thiếu bản dịch, nên gọi `t()` thẳng cho key
// chưa merge vào `i18n/locales/*.json` sẽ hiện chuỗi `feedbackStatusInReview`
// giữa giao diện. Có câu dự phòng tiếng Anh thì giai đoạn "code đã lên, locale
// chưa lên" vẫn đọc được.
//
// ⚠️ Ba bảng thứ tự dưới đây DỰNG TỪ HẰNG ENUM, không dựng từ khoá của
// `summary.by_status` / `by_type` / `by_issue_kind`. Ba dict đó là dict THƯA —
// BE chỉ phát khoá thực sự có bản ghi — nên `Object.keys` chúng sẽ làm trạng
// thái 0 bản ghi biến mất khỏi bộ lọc và khỏi chú giải, đúng lúc người dùng cần
// biết "không còn cái nào" nhất.

import { useCallback } from 'react';
import { FeedbackStatus, FeedbackType } from '@conai/shared-types';
import type { FeedbackRatingValue, IssueKind } from '@conai/shared-types';
import { useI18n, resolveDateLocale } from '../../i18n';
import { translateOrFallback } from '../../utils/apiError';

// ============================================
// Thứ tự hiển thị
// ============================================

/** Trạng thái, xếp theo vòng đời xử lý: mới -> đang xem -> đã xong / bỏ qua. */
export const FEEDBACK_STATUS_ORDER: FeedbackStatus[] = [
  FeedbackStatus.NEW,
  FeedbackStatus.IN_REVIEW,
  FeedbackStatus.RESOLVED,
  FeedbackStatus.DISMISSED,
];

/**
 * Trạng thái mà BE GỬI THÔNG BÁO cho khách khi `PATCH` đặt tới (xác nhận
 * 28/08/2026). `NEW` và `IN_REVIEW` IM LẶNG — "có người bắt đầu đọc" không phải
 * tin tức đáng làm phiền khách.
 *
 * Khai một chỗ và dùng chung cho mọi câu chữ nhắc tới thông báo: hai nơi tự
 * kiểm tra `=== 'RESOLVED'` riêng là hai nơi sẽ lệch nhau vào ngày BE thêm một
 * trạng thái nữa vào danh sách bắn thông báo.
 */
export const FEEDBACK_NOTIFYING_STATUSES: FeedbackStatus[] = [
  FeedbackStatus.RESOLVED,
  FeedbackStatus.DISMISSED,
];

/** `true` nếu chuyển sang trạng thái này làm khách nhận thông báo. */
export function statusNotifiesCustomer(status: FeedbackStatus | null | undefined): boolean {
  return !!status && FEEDBACK_NOTIFYING_STATUSES.includes(status);
}

export const FEEDBACK_TYPE_ORDER: FeedbackType[] = [
  FeedbackType.STORE_EXPERIENCE,
  FeedbackType.PRODUCT_ISSUE,
  FeedbackType.APP_ISSUE,
  FeedbackType.OTHER,
];

// CỐ Ý không có `FEEDBACK_ISSUE_KIND_ORDER`: `issue_kind` KHÔNG phải bộ lọc của
// `/feedback/staff/` (xem `StaffFeedbackFilterParams`), nó chỉ là nhãn đọc thêm
// trên từng dòng. Khai một danh sách thứ tự cho nó chỉ mời người sau dựng một ô
// lọc mà BE nhận rồi bỏ qua im lặng.

/**
 * Mức sao dùng cho bộ lọc và cho biểu đồ phân bố.
 *
 * Khai tại chỗ thay vì import `FEEDBACK_RATING_VALUES`: hằng đó nằm trong chuyến
 * bug #208 nên `packages/shared-types/dist` HIỆN CHƯA có nó (dist đang cũ hơn
 * `src`). Import một hằng chưa tồn tại lúc chạy sẽ cho `undefined.map(...)` —
 * lỗi trắng màn hình chứ không phải lỗi build. Sau khi packages được build lại,
 * đổi dòng dưới thành `[...FEEDBACK_RATING_VALUES]` là xong.
 */
export const FEEDBACK_RATING_ORDER: FeedbackRatingValue[] = [1, 2, 3, 4, 5];

// ============================================
// Nhãn: [key i18n, câu tiếng Anh dự phòng]
// ============================================

type Phrase = readonly [key: string, fallbackEn: string];

const FEEDBACK_STATUS_TEXT: Record<FeedbackStatus, Phrase> = {
  NEW: ['feedbackStatusNew', 'New'],
  IN_REVIEW: ['feedbackStatusInReview', 'In review'],
  RESOLVED: ['feedbackStatusResolved', 'Resolved'],
  DISMISSED: ['feedbackStatusDismissed', 'Dismissed'],
};

const FEEDBACK_TYPE_TEXT: Record<FeedbackType, Phrase> = {
  STORE_EXPERIENCE: ['feedbackTypeStoreExperience', 'Store experience'],
  PRODUCT_ISSUE: ['feedbackTypeProductIssue', 'Product issue'],
  APP_ISSUE: ['feedbackTypeAppIssue', 'App issue'],
  OTHER: ['feedbackTypeOther', 'Other'],
};

const FEEDBACK_ISSUE_KIND_TEXT: Record<IssueKind, Phrase> = {
  EXPIRED: ['feedbackIssueExpired', 'Expired'],
  DAMAGED_PACKAGING: ['feedbackIssueDamagedPackaging', 'Damaged packaging'],
  QUALITY_ISSUE: ['feedbackIssueQualityIssue', 'Quality issue'],
  WRONG_PRICE: ['feedbackIssueWrongPrice', 'Wrong price'],
  MISSING_FROM_SHELF: ['feedbackIssueMissingFromShelf', 'Missing from shelf'],
  OTHER: ['feedbackIssueOther', 'Other'],
};

/** Màu badge trạng thái — dùng chung bảng và modal chi tiết. */
export const FEEDBACK_STATUS_VARIANT: Record<
  FeedbackStatus,
  'success' | 'warning' | 'danger' | 'info' | 'neutral'
> = {
  NEW: 'info',
  IN_REVIEW: 'warning',
  RESOLVED: 'success',
  // DISMISSED KHÔNG dùng `danger`: đỏ trong bảng nghĩa là "có chuyện", còn đây là
  // một quyết định đã khép lại. Cảnh báo đỏ để dành cho nút bấm ra quyết định đó.
  DISMISSED: 'neutral',
};

/** Dấu hiển thị khi một giá trị vắng mặt — dùng thống nhất cả màn. */
export const EMPTY_VALUE = '—';

/**
 * Trần độ dài của `store_reply` VÀ `staff_note` — BE trả 400 nếu vượt.
 *
 * Khai ở đây (chứ không import từ `@conai/shared-types`) vì cùng lý do đã ghi ở
 * `FEEDBACK_RATING_ORDER`: `packages/shared-types/dist` đang cũ hơn `src`, import
 * một hằng chưa tồn tại lúc chạy cho ra `undefined` — mà `maxLength={undefined}`
 * thì textarea KHÔNG chặn gì cả, và lỗi chỉ hiện ra dưới dạng một cái 400 sau khi
 * người dùng đã gõ xong 2500 chữ.
 */
export const FEEDBACK_TEXT_MAX_LENGTH = 2000;

// ============================================
// Định dạng
// ============================================

function toDate(value?: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatFeedbackDate(value: string | null | undefined, locale: string): string {
  const date = toDate(value);
  if (!date) return EMPTY_VALUE;
  return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function formatFeedbackTime(value: string | null | undefined, locale: string): string {
  const date = toDate(value);
  if (!date) return EMPTY_VALUE;
  return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

export function formatFeedbackDateTime(value: string | null | undefined, locale: string): string {
  const date = toDate(value);
  if (!date) return EMPTY_VALUE;
  return date.toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Trích đoạn cho ô `comment` trong bảng — cắt theo từ, không cắt giữa chữ. */
export function excerpt(value: string | null | undefined, maxLength = 90): string | null {
  const text = (value ?? '').replace(/\s+/g, ' ').trim();
  if (!text) return null;
  if (text.length <= maxLength) return text;
  const cut = text.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > maxLength * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/**
 * Rút gọn UUID để hiện trên UI (đơn hàng không có `order_name`).
 * Luôn kèm `title` với giá trị đầy đủ ở phía gọi.
 */
export function shortId(value: string | null | undefined): string {
  if (!value) return EMPTY_VALUE;
  return value.length > 8 ? `${value.slice(0, 8)}…` : value;
}

// ============================================
// Hook dùng trong component
// ============================================

export function useFeedbackText() {
  const { t, language } = useI18n();

  const tr = useCallback(
    (key: string, fallbackEn: string, variables?: Record<string, string | number>) =>
      translateOrFallback(t, key, fallbackEn, variables),
    [t]
  );

  // Tra bảng rồi mới dịch. Giá trị lạ (BE thêm enum mới mà FE chưa biết) thì trả
  // về nguyên mã thay vì ném lỗi — một trạng thái không có nhãn vẫn tốt hơn một
  // bảng trắng.
  const statusLabel = useCallback(
    (status: FeedbackStatus | null | undefined): string => {
      if (!status) return EMPTY_VALUE;
      const phrase = FEEDBACK_STATUS_TEXT[status];
      return phrase ? tr(phrase[0], phrase[1]) : status;
    },
    [tr]
  );

  const typeLabel = useCallback(
    (type: FeedbackType | null | undefined): string => {
      if (!type) return EMPTY_VALUE;
      const phrase = FEEDBACK_TYPE_TEXT[type];
      return phrase ? tr(phrase[0], phrase[1]) : type;
    },
    [tr]
  );

  const issueKindLabel = useCallback(
    (kind: IssueKind | null | undefined): string => {
      if (!kind) return EMPTY_VALUE;
      const phrase = FEEDBACK_ISSUE_KIND_TEXT[kind];
      return phrase ? tr(phrase[0], phrase[1]) : kind;
    },
    [tr]
  );

  const dateLocale = resolveDateLocale(language, 'en-GB');

  const formatDate = useCallback((value?: string | null) => formatFeedbackDate(value, dateLocale), [dateLocale]);
  const formatTime = useCallback((value?: string | null) => formatFeedbackTime(value, dateLocale), [dateLocale]);
  const formatDateTime = useCallback(
    (value?: string | null) => formatFeedbackDateTime(value, dateLocale),
    [dateLocale]
  );

  return { t, tr, statusLabel, typeLabel, issueKindLabel, formatDate, formatTime, formatDateTime };
}
